import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useEffect, useState, useCallback } from "react";
import { http } from "../lib/api";
import {
  CompassTool, SquaresFour, UsersThree, SignOut, User, EnvelopeSimple, Receipt, Buildings, List, X, CalendarCheck, ChatCircleDots, Sun, Key, Compass, FilePdf,
} from "@phosphor-icons/react";
import { MemberAvatar } from "./MemberFormPieces";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ThemeToggle from "./ThemeToggle";
import BroadcastComposeButton from "./BroadcastComposeButton";
import NotificationBell from "./NotificationBell";

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";
  const isMember = user?.role === "member";
  const canSeeRequests = isManagerOrAdmin || isMember;
  const canSeeChat = user?.role === "admin" || user?.role === "manager" || user?.role === "member";
  const [pendingCount, setPendingCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);

  const refreshPending = useCallback(async () => {
    if (!canSeeRequests) return;
    try {
      const { data } = await http.get("/requests?status=pending");
      setPendingCount(data.length);
    } catch (err) {
      // Silent polling: log for devtools instead of toasting every 30 s.
      console.error("refreshPending failed", err);
    }
  }, [canSeeRequests]);

  const refreshChat = useCallback(async () => {
    if (!canSeeChat) return;
    try {
      const { data } = await http.get("/chat/unread");
      setChatUnread(data.total || 0);
    } catch (err) {
      console.error("refreshChat failed", err);
    }
  }, [canSeeChat]);

  useEffect(() => {
    refreshPending();
    if (!canSeeRequests) return;
    const t = setInterval(refreshPending, 30000);
    return () => clearInterval(t);
  }, [canSeeRequests, refreshPending]);

  useEffect(() => {
    refreshChat();
    if (!canSeeChat) return;
    const t = setInterval(refreshChat, 15000);
    return () => clearInterval(t);
  }, [canSeeChat, refreshChat]);

  // Close the drawer whenever the route changes
  useEffect(() => { setMobileNavOpen(false); }, [loc.pathname]);

  // Sync theme to <body> so Radix portals (Toast, Select, Popover, Dialog)
  // inherit the correct palette. Only applied while Layout is mounted so
  // marketing routes never receive the dark/premium class.
  useEffect(() => {
    const classes = ["dark-theme", "premium-theme"];
    classes.forEach((c) => document.body.classList.remove(c));
    if (theme === "dark") document.body.classList.add("dark-theme");
    else if (theme === "premium") document.body.classList.add("premium-theme");
    return () => classes.forEach((c) => document.body.classList.remove(c));
  }, [theme]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (mobileNavOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 border-l-2 ${
      isActive
        ? "border-orange-500 bg-white text-zinc-900"
        : "border-transparent text-zinc-500 hover:text-zinc-900 hover:border-zinc-400"
    } label-overline`;

  const SidebarContent = (
    <>
      <div className="p-4 md:p-5 border-b border-zinc-300 flex items-center gap-3">
        <div className="w-11 h-11 bg-zinc-900 border border-zinc-900 flex items-center justify-center shrink-0">
          <CompassTool size={24} weight="bold" className="text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-black tracking-tighter uppercase text-zinc-900 leading-[1.05]">
            Dream Home
          </div>
          <div className="font-display text-lg font-black tracking-tighter uppercase text-zinc-900 leading-[1.05]">
            Planners
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden p-1.5 text-zinc-500 hover:text-zinc-900"
          data-testid="close-mobile-nav"
          aria-label="Close navigation"
        >
          <X size={18} weight="bold" />
        </button>
      </div>

      <nav className="py-4 flex-1 overflow-y-auto">
        {isManagerOrAdmin && (
          <NavLink to="/glance" data-testid="nav-glance" className={linkClass}>
            <Sun size={18} weight="bold" />
            At a Glance
          </NavLink>
        )}
        <NavLink to="/board" data-testid="nav-board" className={linkClass} end>
          <SquaresFour size={18} weight="bold" />
          Task Board
        </NavLink>
        {isManagerOrAdmin && (
          <NavLink to="/board/projects" data-testid="nav-projects" className={linkClass}>
            <Buildings size={18} weight="bold" />
            Projects
          </NavLink>
        )}
        {canSeeRequests && (
          <NavLink to="/requests" data-testid="nav-requests" className={linkClass}>
            <EnvelopeSimple size={18} weight="bold" />
            <span className="flex-1">Client Requests</span>
            {pendingCount > 0 && (
              <span
                data-testid="pending-request-badge"
                className="ml-auto label-overline bg-orange-600 text-white px-1.5 py-0.5"
              >
                {pendingCount}
              </span>
            )}
          </NavLink>
        )}
        {isManagerOrAdmin && (
          <NavLink to="/invoices" data-testid="nav-invoices" className={linkClass}>
            <Receipt size={18} weight="bold" />
            Invoices
          </NavLink>
        )}
        {isManagerOrAdmin && (
          <NavLink to="/reports" data-testid="nav-reports" className={linkClass}>
            <FilePdf size={18} weight="bold" />
            Reports
          </NavLink>
        )}
        {isManagerOrAdmin && (
          <NavLink to="/team" data-testid="nav-team" className={linkClass}>
            <UsersThree size={18} weight="bold" />
            Team
          </NavLink>
        )}
        <NavLink to="/attendance" data-testid="nav-attendance" className={linkClass}>
          <CalendarCheck size={18} weight="bold" />
          Attendance
        </NavLink>
        <NavLink to="/site-visits" data-testid="nav-site-visits" className={linkClass}>
          <Buildings size={18} weight="bold" />
          Site Visits
        </NavLink>
        {canSeeChat && (
          <NavLink to="/chat" data-testid="nav-chat" className={linkClass}>
            <ChatCircleDots size={18} weight="bold" />
            <span className="flex-1">Chat</span>
            {chatUnread > 0 && (
              <span
                data-testid="chat-unread-badge"
                className="ml-auto label-overline bg-blue-600 text-white px-1.5 py-0.5"
              >
                {chatUnread}
              </span>
            )}
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/marketing-admin" data-testid="nav-marketing" className={linkClass}>
            <Compass size={18} weight="bold" />
            Public Site CMS
          </NavLink>
        )}
        <a href="/" target="_blank" rel="noopener noreferrer"
           data-testid="nav-public-site"
           className="flex items-center gap-3 px-4 py-2.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 label-overline">
          <Compass size={18} weight="bold" />
          View Public Site
        </a>
      </nav>

      <div className="p-4 border-t border-zinc-300 space-y-3">
        <div className="flex items-center gap-3">
          {user?.id ? (
            <MemberAvatar user={user} size={36} className="rounded-none" />
          ) : (
            <div className={`w-9 h-9 flex items-center justify-center text-white ${
              isAdmin ? "bg-orange-600" : user?.role === "manager" ? "bg-blue-700" : "bg-blue-600"
            }`}>
              <User size={18} weight="bold" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-900 truncate">{user?.name}</div>
            <div className="label-overline text-zinc-500 truncate">
              {user?.role === "manager" ? "Project Manager" : user?.role}
            </div>
          </div>
        </div>
        <button
          data-testid="change-password-button"
          onClick={() => setShowChangePwd(true)}
          className="w-full flex items-center justify-center gap-2 border border-zinc-400 bg-white py-2 mb-2 label-overline text-blue-700 hover:bg-blue-600 hover:text-white hover:border-blue-600"
        >
          <Key size={14} weight="bold" /> Change Password
        </button>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); nav("/login"); }}
          className="w-full flex items-center justify-center gap-2 border border-zinc-400 bg-white py-2 label-overline text-zinc-700 hover:bg-zinc-900 hover:text-white hover:border-zinc-900"
        >
          <SignOut size={14} weight="bold" /> Sign Out
        </button>
      </div>
      <ChangePasswordDialog open={showChangePwd} onOpenChange={setShowChangePwd} />
    </>
  );

  return (
    <div className={`theme-scope ${theme === "dark" ? "dark-theme" : theme === "premium" ? "premium-theme" : ""} min-h-screen md:grid md:grid-cols-[240px_1fr] bg-zinc-50`}>
      {/* Desktop sidebar — sticks to viewport so nav scrolls internally
          and Sign Out / user block are always visible without scrolling. */}
      <aside className="hidden md:flex border-r border-zinc-300 bg-zinc-100 flex-col sticky top-0 h-screen self-start">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-zinc-900/60 z-40"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
          <aside
            data-testid="mobile-nav-drawer"
            className="md:hidden fixed inset-y-0 left-0 w-[260px] bg-zinc-100 border-r border-zinc-300 z-50 flex flex-col shadow-2xl"
          >
            {SidebarContent}
          </aside>
        </>
      )}

      {/* Main */}
      <main className="flex flex-col min-w-0">
        <header className="border-b border-zinc-300 bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-6 flex flex-wrap items-start gap-3 sm:gap-4" data-testid="page-header">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-2 -ml-1 border border-zinc-300 bg-white hover:bg-zinc-100 shrink-0 order-1 sm:mt-1"
            data-testid="open-mobile-nav"
            aria-label="Open navigation"
          >
            <List size={16} weight="bold" />
          </button>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 order-1 sm:order-4 ml-auto sm:ml-0">
            <NotificationBell />
            <BroadcastComposeButton />
            <ThemeToggle />
          </div>
          <div className="min-w-0 basis-full sm:basis-0 sm:flex-1 order-2">
            <div className="label-overline text-blue-600 mb-1 sm:mb-2 truncate">
              {isAdmin ? "Administrator · Studio Ops" :
               user?.role === "manager" ? "Project Manager · Studio Ops" :
               "Team Member · My Queue"}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter uppercase text-zinc-900 break-words leading-tight">
              {title}
            </h1>
            {subtitle && <p className="mt-1.5 sm:mt-2 text-sm sm:text-base text-zinc-600 max-w-2xl line-clamp-2 sm:line-clamp-none">{subtitle}</p>}
          </div>
          {actions && (
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto order-3 sm:ml-auto [&>*]:flex-1 sm:[&>*]:flex-none" data-testid="page-actions">
              {actions}
            </div>
          )}
        </header>

        <div className="flex-1 p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
